import { useRef, type KeyboardEvent, type ReactNode } from "react";

/**
 * Etiqueta suelta del sistema (`sk-tag`): un valor elegido con su aspa para
 * quitarlo. El aspa lleva nombre propio («Quitar Cliente»): un «✕» a secas
 * no le dice nada al lector de pantalla.
 */
export function Tag({ children, removeLabel, onRemove }: { children: ReactNode; removeLabel?: string | undefined; onRemove?: (() => void) | undefined }) {
  return (
    <span className="sk-tag">
      {children}
      {onRemove !== undefined && <> <button className="sk-tag-x" type="button" aria-label={removeLabel} onClick={onRemove}>✕</button></>}
    </span>
  );
}

export interface TagsInputProps {
  /** Id del input (lo apunta la etiqueta del campo con `htmlFor`). */
  id: string;
  tags: ReadonlyArray<{ value: string; label: ReactNode; removeLabel: string }>;
  value: string;
  onChange: (v: string) => void;
  /** Enter con texto: quien compone decide si el texto vale y lo añade. */
  onAdd: (text: string) => void;
  onRemove: (value: string) => void;
  placeholder?: string | undefined;
  describedBy?: string | undefined;
  invalid?: boolean | undefined;
}

/**
 * Campo de etiquetas del sistema (`sk-tagsinput`): las elegidas en fila y
 * un input al final. Teclado como la canónica: Enter añade lo escrito,
 * Retroceso con el input vacío quita la última, y un clic en el hueco del
 * campo lleva el foco al input (el campo entero se comporta como un input;
 * el teclado ya llega al input por su cuenta).
 */
export function TagsInput({ id, tags, value, onChange, onAdd, onRemove, placeholder, describedBy, invalid }: TagsInputProps) {
  const input = useRef<HTMLInputElement>(null);
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && value.trim() !== "") { e.preventDefault(); onAdd(value.trim()); }
    const last = tags[tags.length - 1];
    if (e.key === "Backspace" && value === "" && last !== undefined) { e.preventDefault(); onRemove(last.value); }
  };
  return (
    <div className="sk-tagsinput" onClick={(e) => { if (e.target === e.currentTarget) input.current?.focus(); }}>
      {tags.map((t) => (
        <Tag key={t.value} removeLabel={t.removeLabel} onRemove={() => { onRemove(t.value); input.current?.focus(); }}>{t.label}</Tag>
      ))}
      <input
        ref={input} id={id} value={value} placeholder={placeholder} autoComplete="off"
        aria-describedby={describedBy} aria-invalid={invalid === true ? true : undefined}
        onChange={(e) => onChange(e.target.value)} onKeyDown={key}
      />
    </div>
  );
}
